// Client-side PDF path — ported 1:1 from apps/web/src/lib/resumeToPrint.ts
// (the swappable seam that file documents: a later server-stored PDF replaces
// ONLY this function). Clone the rendered ResumeView node into a clean popup
// window and trigger the browser's print dialog → "Save as PDF". Text stays
// selectable + ATS-clean.
//
// THE ONE PORT DIFFERENCE (deliberate): throughline's ResumeView styles route
// through the app's --tl-* theme vars, which are DEFINED ON THE APP's root —
// a bare popup window has none of them, so the cloned CV would print with
// unset colors. The popup therefore pins PRINT-SAFE values for exactly the
// five vars the résumé <style> block consumes. These are print-medium
// constants (near-black ink on white paper — correct in BOTH app themes; a
// dark-mode wall of white-on-black would be wrong on paper), not UI theme
// values — the no-hardcoded-colors law governs the APP's surfaces, and the
// printed page is its own medium. Swapping the app theme never changes what
// a printed CV should look like.

import { RESUME_VIEW_DOM_ID } from '../components/resume/ResumeView';

const PRINT_SAFE_VARS =
  '--tl-surf:#ffffff;--tl-ink:#1c1c1c;--tl-ink-soft:#555555;--tl-accent:#20635a;--tl-line:#dddddd;';

/**
 * Print the currently-rendered ResumeView. Opens a print-only window with just
 * the CV markup + its self-contained styles. Returns false if the popup was
 * blocked or the CV node isn't on the page — the caller reports that honestly
 * (never a silent no-op).
 */
export function printResume(documentTitle = 'Resume'): boolean {
  const node = document.getElementById(RESUME_VIEW_DOM_ID);
  if (!node) return false;

  const win = window.open('', '_blank', 'width=900,height=1200');
  if (!win) return false;

  // The CV node includes its own <style> block, so outerHTML is
  // self-sufficient apart from the pinned print vars + a page reset.
  win.document.open();
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(
      documentTitle,
    )}</title><style>:root{${PRINT_SAFE_VARS}}html,body{margin:0;padding:0;background:#fff;}</style></head><body>${node.outerHTML}</body></html>`,
  );
  win.document.close();

  // Give the cloned styles a tick to apply, then print. onafterprint closes.
  win.focus();
  win.onafterprint = () => win.close();
  setTimeout(() => {
    win.print();
  }, 350);
  return true;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}
